import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { endpoints } from '@rossum/api-client';
import { SchemaSection } from '@rossum/api-client/schemas';
import { IconCode, IconCodeOff } from '@rossum/ui/icons/tabler';
import {
  Autocomplete,
  Button,
  Chip,
  Collapse,
  IconButton,
  Stack,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { useApiClient } from '../../../../../lib/apiClient';
import { FormulaEditor } from '../../../../formulas/FormulaEditor/FormulaEditor';
import { getValidSchemaFields } from '../../../../formulas/FormulaEditor/helpers';
import {
  dependenciesToIndexFormula,
  isCanonicalIndexFormula,
  parseCanonicalIndexFormula,
} from './indexFormulaUtils';

type IndexFormulaProps = {
  schemaContent: SchemaSection[];
  value: string;
  onChange: (s: string) => void;
  currentSchemaId: string;
};

const useFormulaInfo = (schemaContent: SchemaSection[]) => {
  const api = useApiClient();
  return useQuery({
    queryKey: ['formulas-info', schemaContent],
    queryFn: () =>
      api.request(
        endpoints.internal.schemas.formulasInfo({
          schemaContent,
        })
      ),
    enabled: schemaContent.length > 0,
  });
};

export const IndexFormula = ({
  schemaContent,
  value,
  onChange,
  currentSchemaId,
}: IndexFormulaProps) => {
  const intl = useIntl();
  const formulasInfoQuery = useFormulaInfo(schemaContent);

  const computedFieldDependencies = useMemo(() => {
    const formulaInfo = formulasInfoQuery.data?.results.find(
      f => f.id === currentSchemaId
    );
    return formulaInfo?.dependencies ?? [];
  }, [formulasInfoQuery.data?.results, currentSchemaId]);

  const isCanonical = isCanonicalIndexFormula(value, currentSchemaId);
  const parsed = isCanonical ? parseCanonicalIndexFormula(value) : null;

  const [showCode, setShowCode] = useState(!isCanonical && !!value);

  const selectedDependencies = parsed?.dependencies ?? [];

  const validFields = useMemo(
    () =>
      getValidSchemaFields(schemaContent)
        .filter(f => f.category === 'datapoint' && f.id !== currentSchemaId)
        .map(f => f.id),
    [schemaContent, currentSchemaId]
  );

  const handleDependenciesChange = (newDependencies: string[]) => {
    onChange(dependenciesToIndexFormula(currentSchemaId, newDependencies));
  };

  const handleUseComputedFieldDependencies = () => {
    onChange(
      dependenciesToIndexFormula(currentSchemaId, computedFieldDependencies)
    );
  };

  const handleToggleCode = () => {
    setShowCode(prev => !prev);
  };

  const isNonCanonical = !isCanonical && !!value;

  return (
    <Stack spacing={2} pt={2}>
      <Stack spacing={1}>
        {isNonCanonical ? (
          <Typography
            variant="body2"
            height={40}
            sx={{
              placeContent: 'center',
            }}
          >
            {intl.formatMessage({
              id: 'features.queueSettings.fields.memory.indexFormula.customFormula',
            })}
          </Typography>
        ) : (
          <Autocomplete
            multiple
            options={validFields}
            value={selectedDependencies}
            onChange={(_, newValue) => handleDependenciesChange(newValue)}
            renderInput={params => (
              <TextField
                {...params}
                label={intl.formatMessage({
                  id: 'features.queueSettings.fields.memory.indexFormula.selectDeps',
                })}
                size="small"
              />
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip key={key} label={option} size="small" {...tagProps} />
                );
              })
            }
            size="small"
          />
        )}

        <Collapse in={showCode} mountOnEnter>
          <FormulaEditor
            placeholder={intl.formatMessage({
              id: 'features.queueSettings.fields.memory.indexFormula.title',
            })}
            fields={getValidSchemaFields(schemaContent)}
            value={value}
            config={{
              maxVisibleLines: 1,
              minVisibleLines: 1,
              hideToolbar: true,
            }}
            onChange={onChange}
          />
        </Collapse>
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={2}
      >
        <Tooltip
          title={intl.formatMessage({
            id: showCode
              ? 'features.formulas.hideSourceCode'
              : 'features.formulas.showSourceCode',
          })}
        >
          <IconButton color="inherit" onClick={handleToggleCode}>
            <SvgIcon fontSize="large">
              {showCode ? <IconCodeOff /> : <IconCode />}
            </SvgIcon>
          </IconButton>
        </Tooltip>
        <Tooltip
          title={intl.formatMessage({
            id: 'features.queueSettings.fields.memory.indexFormula.inferTooltip',
          })}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleUseComputedFieldDependencies}
          >
            {intl.formatMessage({
              id: 'features.queueSettings.fields.memory.indexFormula.infer',
            })}
          </Button>
        </Tooltip>
      </Stack>
    </Stack>
  );
};
